import * as Yup from "yup";
import { FocusEvent, useEffect, useMemo, useState } from "react";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { FormikErrors, FormikProps, FormikTouched, Formik } from "formik";
import { SSN4Input } from "./ssn4-input";


type SSN4InputContainerProps = {
    title: string;
    attribute: AttributeFormState;
    fieldName: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
};

export const SSN4InputContainer = (props: SSN4InputContainerProps) => {
    const {
        title,
        attribute,
        fieldName,
        setFieldValue,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const isRequired = useMemo(() => !(attribute.isOptional || attribute.optional), [attribute.isOptional, attribute.optional]);
    const [formInitialValue] = useState({ ...attribute.inputData });
    const schema = useMemo(() => {
        return isRequired ?
            Yup.object().shape({
                serialNumber: Yup.string()
                    .test('isNumber', 'validation.serialNumber.isNumber', (value: string) => /^\d+$/.test(value))
                    .length(4, 'validation.serialNumber.length')
                    .required('validation.serialNumber.required')
            }) : null;
    }, [isRequired]);

    useEffect(() => {
        const { ssn4 } = attribute.inputData;
        setFieldValue(fieldName, ssn4);
    }, [attribute.inputData.ssn4]);


    if (!attribute.isNew) {
        return null;
    }

    const renderSSN4Input = (
        errors: FormikErrors<any>,
        touched: FormikTouched<any>,
        setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>
    ) => {
        return (
            <SSN4Input
                title={title}
                attribute={attribute}
                errors={errors}
                onBlur={onBlur}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                touched={touched}
                isSubmitting={isSubmitting}
                onSetSubmitValue={onSetSubmitValue}
            />
        );
    };

    return (
        <Formik
            initialValues={formInitialValue}
            validateOnMount={true}
            validationSchema={schema}
            onSubmit={undefined}
        >
            {({ errors, touched, setFieldTouched, setFieldValue }: FormikProps<any>) => (
                renderSSN4Input(
                    errors,
                    touched,
                    setFieldTouched,
                    setFieldValue
                )
            )}
        </Formik>
    );
};